import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { type Tool } from '@/data/tools';
import * as LucideIcons from 'lucide-react';
import { useState } from 'react';

type ToolCardProps = {
  tool: Tool;
  onClick: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: (toolId: string) => void;
};

export function ToolCard({ tool, onClick, isFavorite = false, onToggleFavorite }: ToolCardProps) {
  const Icon = LucideIcons[tool.icon as keyof typeof LucideIcons] as any;
  const isInternal = tool.type === 'internal';
  const [infoOpen, setInfoOpen] = useState(false);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.(tool.id);
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleInfoHover = () => {
    // On desktop (hover capable), open popover on hover
    if (window.matchMedia('(hover: hover)').matches) {
      setInfoOpen(true);
    }
  };

  const handleInfoLeave = () => {
    // On desktop, close popover when mouse leaves
    if (window.matchMedia('(hover: hover)').matches) {
      setInfoOpen(false);
    }
  };

  return (
    <Card
      className='group cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary/50 h-full flex flex-col relative overflow-hidden'
      onClick={onClick}
    >
      {/* Subtle gradient background on hover */}
      <div className='absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200' />

      <CardHeader className='relative space-y-2'>
        {/* Top row: Icon, Title, Actions */}
        <div className='flex items-center gap-3'>
          {/* Icon */}
          {Icon && (
            <div
              className={`p-2 rounded-lg shrink-0 transition-all duration-200 ${
                isInternal ?
                  'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/40'
                : 'bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40'
              }`}
            >
              <Icon className='h-5 w-5' />
            </div>
          )}

          {/* Title */}
          <div className='flex-1 min-w-0'>
            <CardTitle className='text-base font-semibold leading-tight group-hover:text-primary transition-colors line-clamp-2'>
              {tool.name}
            </CardTitle>
          </div>

          {/* Actions: Favorite + Info */}
          <div className='flex items-center gap-0.5 shrink-0'>
            {/* Favorite button */}
            {onToggleFavorite && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-8 w-8 hover:bg-transparent'
                      onClick={handleFavoriteClick}
                    >
                      <LucideIcons.Star
                        className={`h-4 w-4 transition-all ${
                          isFavorite ?
                            'fill-yellow-500 text-yellow-500'
                          : 'text-muted-foreground hover:text-yellow-500'
                        }`}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='top'>
                    <p className='text-xs'>{isFavorite ? 'Remove from favorites' : 'Add to favorites'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Info button - hover on desktop, click on mobile */}
            <Popover
              open={infoOpen}
              onOpenChange={setInfoOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8 hover:bg-transparent'
                  onClick={handleInfoClick}
                  onMouseEnter={handleInfoHover}
                  onMouseLeave={handleInfoLeave}
                >
                  <LucideIcons.Info className='h-4 w-4 text-muted-foreground hover:text-primary transition-colors' />
                </Button>
              </PopoverTrigger>

              <PopoverContent
                side='top'
                className='w-72 p-4'
                onClick={(e) => e.stopPropagation()}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onMouseEnter={() => setInfoOpen(true)}
                onMouseLeave={handleInfoLeave}
              >
                <div className='space-y-3'>
                  <div className='flex items-start gap-2'>
                    {Icon && <Icon className='h-4 w-4 text-muted-foreground shrink-0 mt-0.5' />}
                    <div>
                      <h4 className='text-sm font-semibold mb-1'>{tool.name}</h4>
                      <p className='text-xs text-muted-foreground leading-relaxed'>{tool.description}</p>
                    </div>
                  </div>
                  <div className='flex items-center gap-2 pt-2 border-t'>
                    <Badge
                      variant='outline'
                      className='text-xs'
                    >
                      {tool.category}
                    </Badge>
                    <Badge
                      variant='outline'
                      className='text-xs'
                    >
                      {isInternal ? '🏠 Local' : '🌐 External'}
                    </Badge>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Bottom: Type badge */}
        <div className='flex items-center'>
          <Badge
            variant='secondary'
            className={`text-xs transition-colors ${
              isInternal ?
                'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800'
              : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800 flex items-center gap-1'
            }`}
          >
            {isInternal ?
              'Custom'
            : <>
                External
                <LucideIcons.ExternalLink className='h-3 w-3' />
              </>
            }
          </Badge>
        </div>
      </CardHeader>
    </Card>
  );
}
